import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { CHAT_LIMITS } from '@twinden/shared';
import { AvatarUpload } from '../family/AvatarUpload';
import { ProfilePhotoViewer } from './PeopleSheet';
import { KeysNotReadyError, saveGroupInfo } from '../../lib/e2ee/groupInfo';
import { pictureErrorMessage, setGroupPicture } from '../../lib/pictureSetting';
import { Crown, LogOut, Trash2, UserMinus, UserPlus } from 'lucide-react';
import {
  api,
  ApiError,
  type FamilyMember,
  type FamilyRole,
  type SealedDescription,
} from '../../lib/api';
import { t as tNow, useT } from '../../i18n';
import type { OpenedE2EE } from '../../lib/e2ee/session';

export interface ChatParticipant {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  lastSeenAt: string | null;
  role: 'admin' | 'member';
}

export interface ChatConversation {
  id: string;
  familyId: string;
  type: 'direct' | 'group';
  name: string | null;
  avatarUrl: string | null;
  isDefault: boolean;
  lastMessageAt: string | null;
  unreadCount?: number;
  /** Latest visible message, for the home tiles (Living Room). */
  lastMessage?: {
    senderId: string;
    senderName: string;
    type: string;
    preview: string;
    /** An album's counts (M60, D219), for "Sent 4 photos". */
    album?: { photos: number; videos: number };
    /** A system line's event, to word it on the tile (D234). */
    metadata?: Record<string, unknown> | null;
    createdAt: string;
    /** Set by this device's opener only: `bound: false` is older (D328). */
    e2ee?: OpenedE2EE;
  } | null;
  participants: ChatParticipant[];
  /** A DM's other person, after they deleted their account (M51). */
  formerParticipants?: Array<{
    id: string;
    handle: string | null;
    displayName: string;
    avatarUrl: string | null;
    deleted?: true;
  }>;
  createdAt: string;
  // M63, my own settings for this chat (D231, D232).
  archivedAt?: string | null;
  keepArchived?: boolean;
  markedUnread?: boolean;
  /** M64: pinned to the top of the home since then (D237). */
  pinnedAt?: string | null;
  /** M64: new messages here disappear after this long (D238). */
  disappearingSeconds?: number | null;
  /** Sealed group description, opened in place (D234). */
  description?: SealedDescription | null;
}

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : tNow('common.somethingWrong');
}

interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NewGroupDialogProps extends BaseDialogProps {
  familyId: string;
  members: FamilyMember[];
  currentUserId: string;
  onCreated: (conversationId: string) => void;
}

export function NewGroupDialog({
  isOpen,
  onClose,
  familyId,
  members,
  currentUserId,
  onCreated,
}: NewGroupDialogProps) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const t = useT();
  const subtle = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelected([]);
    }
  }, [isOpen]);

  const candidates = members.filter((m) => m.userId !== currentUserId);

  const toggle = (userId: string) => {
    setSelected((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const submit = async () => {
    if (!name.trim() || selected.length === 0) return;
    setBusy(true);
    try {
      const res = await api.createFamilyConversation(familyId, {
        type: 'group',
        name: name.trim(),
        participantIds: selected,
      });
      toast({ title: t('chat.dialogs.groupCreated'), status: 'success' });
      onCreated(res.data.id);
      onClose();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotCreateGroup'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('chat.dialogs.newGroup')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t('chat.dialogs.groupName')}</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('chat.dialogs.groupNamePlaceholder')}
                maxLength={100}
              />
            </FormControl>

            <Box>
              <Text fontWeight="medium" mb={2}>
                {t('chat.dialogs.members')}
              </Text>
              {candidates.length === 0 ? (
                <Text fontSize="sm" color={subtle}>
                  {t('chat.dialogs.nobodyElse')}
                </Text>
              ) : (
                <VStack
                  align="stretch"
                  spacing={1}
                  maxH="280px"
                  overflowY="auto"
                >
                  {candidates.map((member) => (
                    <Checkbox
                      key={member.userId}
                      isChecked={selected.includes(member.userId)}
                      onChange={() => toggle(member.userId)}
                    >
                      <HStack spacing={2}>
                        <Avatar
                          size="xs"
                          name={member.user.displayName}
                          src={member.user.avatarUrl ?? undefined}
                          ownerId={member.userId}
                          familyId={familyId}
                        />
                        <Text>{member.user.displayName}</Text>
                        <Text fontSize="xs" color={subtle}>
                          @{member.user.handle}
                        </Text>
                      </HStack>
                    </Checkbox>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={submit}
            isDisabled={!name.trim() || selected.length === 0}
            isLoading={busy}
          >
            {t('chat.dialogs.createGroup')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface NewChatDialogProps extends BaseDialogProps {
  familyId: string;
  members: FamilyMember[];
  currentUserId: string;
  onCreated: (conversationId: string) => void;
}

export function NewChatDialog({
  isOpen,
  onClose,
  familyId,
  members,
  currentUserId,
  onCreated,
}: NewChatDialogProps) {
  const [query, setQuery] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const toast = useToast();
  const t = useT();
  const subtle = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (isOpen) setQuery('');
  }, [isOpen]);

  const candidates = useMemo(() => {
    const others = members.filter((m) => m.userId !== currentUserId);
    if (!query.trim()) return others;
    const needle = query.trim().toLowerCase();
    return others.filter(
      (m) =>
        m.user.displayName.toLowerCase().includes(needle) ||
        m.user.handle.toLowerCase().includes(needle)
    );
  }, [members, currentUserId, query]);

  const start = async (userId: string) => {
    setBusyUserId(userId);
    try {
      const res = await api.createFamilyConversation(familyId, {
        type: 'direct',
        userId,
      });
      onCreated(res.data.id);
      onClose();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotOpenChat'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('chat.dialogs.newChat')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('chat.dialogs.searchMembers')}
            />
            {candidates.length === 0 ? (
              <Text fontSize="sm" color={subtle} py={3}>
                {t('chat.dialogs.noMatch')}
              </Text>
            ) : (
              <VStack align="stretch" spacing={1} maxH="320px" overflowY="auto">
                {candidates.map((member) => (
                  <Button
                    key={member.userId}
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => start(member.userId)}
                    isLoading={busyUserId === member.userId}
                    leftIcon={
                      <Avatar
                        size="sm"
                        name={member.user.displayName}
                        src={member.user.avatarUrl ?? undefined}
                        ownerId={member.userId}
                        familyId={familyId}
                      />
                    }
                  >
                    <Box textAlign="left">
                      <Text>{member.user.displayName}</Text>
                      <Text fontSize="xs" color={subtle}>
                        @{member.user.handle}
                      </Text>
                    </Box>
                  </Button>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface GroupSettingsDialogProps extends BaseDialogProps {
  conversation: ChatConversation | null;
  members: FamilyMember[];
  currentUserId: string;
  familyRole: FamilyRole;
  onChanged: () => void;
  onClosed: () => void;
}

export function GroupSettingsDialog({
  isOpen,
  onClose,
  conversation,
  members,
  currentUserId,
  familyRole,
  onChanged,
  onClosed,
}: GroupSettingsDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [addIds, setAddIds] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const t = useT();
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const panelBg = useColorModeValue('gray.50', 'gray.700');

  // The sealed description, opened on this device (D234).
  const sealed = conversation?.description ?? null;
  // Only this chat's own description: one carrying another chat's id is
  // treated as unreadable, never shown here.
  // Shown only once this device opened it: descriptions are always sealed
  // (D234), so one that arrives as plain text is never shown (D347).
  const descriptionLocked =
    sealed !== null &&
    (!sealed.e2ee?.envelope || sealed.conversationId !== conversation?.id);
  const savedDescription = sealed && !descriptionLocked ? sealed.content : '';
  // Only when the sheet opens or another chat is shown: a live update
  // mustn't wipe what's being typed.
  // A member's picture, big (D279).
  const [photoOf, setPhotoOf] = useState<ChatParticipant | null>(null);
  const conversationId = conversation?.id;
  useEffect(() => {
    if (isOpen && conversation) {
      setName(conversation.name ?? '');
      setDescription(savedDescription);
      setAddIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, conversationId]);

  if (!conversation) return null;

  const me = conversation.participants.find((p) => p.id === currentUserId);
  const familyAdmin = familyRole === 'root' || familyRole === 'admin';
  const isDefault = conversation.isDefault;
  // Members, roles, leaving and deleting: group admins and family
  // roots/admins (D11); the family group has none of them (D10).
  const canManage = !isDefault && (me?.role === 'admin' || familyAdmin);
  // Picture, name and description: anyone in a group; the family group is
  // the family's roots and admins (D234).
  const canEditInfo = isDefault ? familyAdmin : true;
  const participantIds = new Set(conversation.participants.map((p) => p.id));
  const addable = members.filter((m) => !participantIds.has(m.userId));
  const trimmedName = name.trim();
  const nameChanged = trimmedName !== '' && trimmedName !== conversation.name;
  const descriptionChanged =
    !descriptionLocked && description.trim() !== savedDescription.trim();

  const saveInfo = async () => {
    if (!nameChanged && !descriptionChanged) return;
    setBusy('info');
    try {
      await saveGroupInfo(conversation.id, {
        ...(nameChanged ? { name: trimmedName } : {}),
        ...(descriptionChanged ? { description } : {}),
      });
      toast({ title: t('chat.info.saved'), status: 'success' });
      onChanged();
    } catch (err) {
      toast({
        title: t('chat.info.saveFailed'),
        description:
          err instanceof KeysNotReadyError
            ? t('chat.info.keysNotReady')
            : errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  // Sealed with the chat's key on this device first (D291).
  const savePicture = async (picture: string | null) => {
    setBusy('picture');
    try {
      await setGroupPicture(conversation.id, picture);
      toast({
        title: picture
          ? t('chat.info.pictureUpdated')
          : t('chat.info.pictureRemoved'),
        status: 'success',
      });
      onChanged();
    } catch (err) {
      toast({
        title: t('chat.info.pictureFailed'),
        description: pictureErrorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const addMembers = async () => {
    if (addIds.length === 0) return;
    setBusy('add');
    try {
      await api.addConversationParticipants(conversation.id, addIds);
      toast({ title: t('chat.dialogs.membersAdded'), status: 'success' });
      setAddIds([]);
      onChanged();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotAdd'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const remove = async (userId: string) => {
    if (!window.confirm(t('chat.dialogs.confirmRemove'))) return;
    setBusy(`remove:${userId}`);
    try {
      await api.removeConversationParticipant(conversation.id, userId);
      onChanged();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotRemove'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const setRole = async (userId: string, role: 'admin' | 'member') => {
    setBusy(`role:${userId}`);
    try {
      await api.setConversationParticipantRole(conversation.id, userId, role);
      onChanged();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotChangeRole'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const leave = async () => {
    if (!window.confirm(t('chat.dialogs.confirmLeave'))) return;
    setBusy('leave');
    try {
      await api.leaveConversation(conversation.id);
      toast({ title: t('chat.dialogs.leftGroup'), status: 'success' });
      onChanged();
      onClose();
      onClosed();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotLeave'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const removeGroup = async () => {
    if (!window.confirm(t('chat.dialogs.confirmDelete'))) return;
    setBusy('delete');
    try {
      await api.deleteConversation(conversation.id);
      toast({ title: t('chat.dialogs.groupDeleted'), status: 'success' });
      onChanged();
      onClose();
      onClosed();
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotDelete'),
        description: errorMessage(err),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('chat.header.groupSettings')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={5}>
              {canEditInfo ? (
                <VStack align="stretch" spacing={4}>
                  <Box alignSelf="center" aria-label={t('chat.info.picture')}>
                    <AvatarUpload
                      value={conversation.avatarUrl}
                      name={conversation.name ?? undefined}
                      ownerId={conversation.id}
                      familyId={conversation.familyId}
                      onChange={(value) => void savePicture(value)}
                    />
                  </Box>
                  <FormControl>
                    <FormLabel>{t('chat.dialogs.groupName')}</FormLabel>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={100}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('chat.info.description')}</FormLabel>
                    {descriptionLocked ? (
                      <Text color={subtle} fontSize="sm">
                        {t('chat.info.descriptionLocked')}
                      </Text>
                    ) : (
                      <>
                        <Textarea
                          value={description}
                          onChange={(e) =>
                            setDescription(
                              e.target.value.slice(
                                0,
                                CHAT_LIMITS.DESCRIPTION_MAX
                              )
                            )
                          }
                          maxLength={CHAT_LIMITS.DESCRIPTION_MAX}
                          placeholder={t('chat.info.descriptionPlaceholder')}
                          rows={3}
                          resize="vertical"
                        />
                        <Text fontSize="xs" color={subtle} textAlign="right">
                          {t('chat.info.descriptionCount', {
                            n: description.length,
                            max: CHAT_LIMITS.DESCRIPTION_MAX,
                          })}
                        </Text>
                      </>
                    )}
                  </FormControl>
                  <Button
                    onClick={() => void saveInfo()}
                    isLoading={busy === 'info'}
                    isDisabled={!nameChanged && !descriptionChanged}
                    alignSelf="flex-end"
                  >
                    {t('common.save')}
                  </Button>
                  <Text fontSize="sm" color={subtle}>
                    {isDefault
                      ? t('chat.info.adminsOnlyNote')
                      : t('chat.info.encryptedNote')}
                  </Text>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <Avatar
                    alignSelf="center"
                    size="xl"
                    name={conversation.name ?? undefined}
                    src={conversation.avatarUrl ?? undefined}
                    ownerId={conversation.id}
                    familyId={conversation.familyId}
                  />
                  <Box>
                    <Text fontSize="sm" color={subtle}>
                      {t('chat.dialogs.name')}
                    </Text>
                    <Text fontWeight="medium">{conversation.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={subtle}>
                      {t('chat.info.description')}
                    </Text>
                    <Text whiteSpace="pre-wrap">
                      {descriptionLocked
                        ? t('chat.info.descriptionLocked')
                        : savedDescription || t('chat.info.noDescription')}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color={subtle}>
                    {t('chat.info.adminsOnlyNote')}
                  </Text>
                </VStack>
              )}

              <Box>
                <Text fontWeight="medium" mb={2}>
                  {t('chat.dialogs.membersCount', {
                    n: conversation.participants.length,
                  })}
                </Text>
                <VStack align="stretch" spacing={1}>
                  {conversation.participants.map((participant) => (
                    <HStack
                      key={participant.id}
                      bg={panelBg}
                      px={3}
                      py={2}
                      borderRadius="md"
                      spacing={3}
                    >
                      <Box
                        as="button"
                        type="button"
                        borderRadius="full"
                        aria-label={t('chat.people.seePhoto', {
                          name: participant.displayName,
                        })}
                        onClick={() => setPhotoOf(participant)}
                      >
                        <Avatar
                          size="sm"
                          name={participant.displayName}
                          src={participant.avatarUrl ?? undefined}
                          ownerId={participant.id}
                          familyId={conversation.familyId}
                        />
                      </Box>
                      <Box flex="1" overflow="hidden">
                        <HStack spacing={2}>
                          <Text fontWeight="medium" noOfLines={1}>
                            {participant.displayName}
                            {participant.id === currentUserId
                              ? ` ${t('chat.dialogs.youSuffix')}`
                              : ''}
                          </Text>
                          {participant.role === 'admin' && (
                            <Badge colorScheme="purple">
                              <HStack spacing={1}>
                                <Crown size={10} />
                                <Text>{t('chat.dialogs.admin')}</Text>
                              </HStack>
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color={subtle}>
                          @{participant.handle}
                        </Text>
                      </Box>

                      {canManage && participant.id !== currentUserId && (
                        <HStack spacing={1}>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() =>
                              setRole(
                                participant.id,
                                participant.role === 'admin'
                                  ? 'member'
                                  : 'admin'
                              )
                            }
                            isLoading={busy === `role:${participant.id}`}
                          >
                            {participant.role === 'admin'
                              ? t('chat.dialogs.demote')
                              : t('chat.dialogs.promote')}
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => remove(participant.id)}
                            isLoading={busy === `remove:${participant.id}`}
                          >
                            <UserMinus size={14} />
                          </Button>
                        </HStack>
                      )}
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {canManage && addable.length > 0 && (
                <Box>
                  <Text fontWeight="medium" mb={2}>
                    {t('chat.dialogs.addMembers')}
                  </Text>
                  <VStack
                    align="stretch"
                    spacing={1}
                    maxH="180px"
                    overflowY="auto"
                  >
                    {addable.map((member) => (
                      <Checkbox
                        key={member.userId}
                        isChecked={addIds.includes(member.userId)}
                        onChange={() =>
                          setAddIds((prev) =>
                            prev.includes(member.userId)
                              ? prev.filter((id) => id !== member.userId)
                              : [...prev, member.userId]
                          )
                        }
                      >
                        {member.user.displayName} @{member.user.handle}
                      </Checkbox>
                    ))}
                  </VStack>
                  <Button
                    mt={2}
                    size="sm"
                    leftIcon={<UserPlus size={14} />}
                    onClick={addMembers}
                    isLoading={busy === 'add'}
                    isDisabled={addIds.length === 0}
                  >
                    {t('chat.dialogs.addSelected')}
                  </Button>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter justifyContent="space-between">
            <HStack>
              {!isDefault && (
                <Button
                  leftIcon={<LogOut size={16} />}
                  variant="outline"
                  onClick={leave}
                  isLoading={busy === 'leave'}
                >
                  {t('chat.dialogs.leave')}
                </Button>
              )}
              {canManage && (
                <Button
                  leftIcon={<Trash2 size={16} />}
                  colorScheme="red"
                  variant="outline"
                  onClick={removeGroup}
                  isLoading={busy === 'delete'}
                >
                  {t('common.delete')}
                </Button>
              )}
            </HStack>
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ProfilePhotoViewer
        person={photoOf}
        familyId={conversation.familyId}
        onClose={() => setPhotoOf(null)}
      />
    </>
  );
}
